import React from 'react';
import { FiCheckCircle, FiGlobe, FiUsers, FiClock, FiActivity, FiAward, FiZap, FiBarChart2, FiMap, FiShield } from 'react-icons/fi';
import { FaGraduationCap } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const Features = () => {
  const mainFeatures = [
    { title: "Diseñado para Hispanohablantes", desc: "Currículo que aborda retos específicos de mexicanos: pronunciación y gramática contrastiva.", icon: FiGlobe, color: "text-green-500" },
    { title: "Profesores Nativos Certificados", desc: "Expertos con certificación internacional y experiencia enseñando a latinos.", icon: FaGraduationCap, color: "text-red-500" },
    { title: "Conversación Real desde Día 1", desc: "Sin memorizar listas. Práctica auténtica para construir confianza inmediata.", icon: FiUsers, color: "text-yellow-500" },
    { title: "Flexibilidad Total 24/7", desc: "Sin horarios fijos. Entra cuando quieras desde PC, tablet o celular.", icon: FiClock, color: "text-blue-500" },
    { title: "Resultados Medibles", desc: "Trackeo en tiempo real. Pruebas de nivel cada 40 horas para garantizar avance.", icon: FiActivity, color: "text-purple-500" },
    { title: "Precio Justo México-Friendly", desc: "Desde $175 MXN/clase. Más accesible que escuelas tradicionales.", icon: FiZap, color: "text-orange-500" }
  ];

  const secondaryFeatures = [
    { title: "Dashboard de Progreso", desc: "Visualiza tu avance en pronunciación y gramática día a día.", icon: FiBarChart2 },
    { title: "Plan Personalizado", desc: "Tu ruta de aprendizaje se adapta a tus objetivos y disponibilidad.", icon: FiMap },
    { title: "Comunidad Global", desc: "Conecta con estudiantes de todo el mundo en salas 24/7.", icon: FiGlobe },
    { title: "Certificados Internacionales", desc: "Preparación para TOEFL, IELTS y diplomas de fluidez.", icon: FiShield }
  ];

  return (
    <section className="py-24 px-4 bg-zinc-950 border-t border-zinc-900 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase leading-none">
            ¿Por Qué <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-yellow-500 to-green-600">OnlineClassesEnglish?</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Somos la universidad online que México estaba esperando: accesible, flexible y sin burocracia.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {mainFeatures.map((f, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl hover:border-yellow-500/30 transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                <SafeIcon icon={f.icon} className="text-8xl text-white" />
              </div>
              <div className={`w-14 h-14 bg-black rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-inner border border-zinc-800`}>
                <SafeIcon icon={f.icon} className={`text-3xl ${f.color}`} />
              </div>
              <h3 className="text-xl font-black text-white mb-3 uppercase tracking-tight relative z-10">{f.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed relative z-10">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {secondaryFeatures.map((f, i) => (
            <div key={i} className="text-center p-6 bg-zinc-900/40 rounded-2xl border border-zinc-800 hover:bg-zinc-900 transition-colors">
              <SafeIcon icon={f.icon} className="text-2xl text-yellow-500 mx-auto mb-3" />
              <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-tighter">{f.title}</h4>
              <p className="text-[10px] text-gray-500 leading-tight">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* How it Works / 4 Steps Section */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <h3 className="text-2xl md:text-4xl font-black text-white uppercase">Cómo Funciona en <span className="text-red-500">4 Pasos</span></h3>
            <p className="text-gray-500 mt-2">Simple, rápido y diseñado para tu éxito.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Crea tu Cuenta", desc: "Regístrate en minutos y haz tu diagnóstico rápido." },
              { step: "2", title: "Elige tu Plan", desc: "Selecciona clases 1-a-1, intensivos o pase de práctica." },
              { step: "3", title: "Entra a Clases", desc: "Sin descargas. Un clic y estás dentro del aula virtual." },
              { step: "4", title: "Practica 24/7", desc: "Usa las salas de conversación ilimitadas a cualquier hora." }
            ].map((s, i) => (
              <div key={i} className="relative pt-12 text-center group">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center text-2xl font-black text-white group-hover:bg-yellow-500 group-hover:text-black transition-all">
                  {s.step}
                </div>
                <h4 className="text-white font-bold mb-2 uppercase text-sm tracking-widest">{s.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                {i < 3 && <div className="hidden md:block absolute top-8 left-[70%] w-[60%] h-px bg-zinc-800"></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;